// ./src/hooks/useBulkJobs.ts
//
// ROADMAP #132 — the background jobs of ONE kind (on one event, when given): the ones still running, and the finished
// ones whose report the host has not closed yet. Read when the screen opens — so a host who left mid-job and came back
// sees where it got to — and re-read every few seconds while one is still running, only while the tab is visible.
// Generic: a guest import today, a membership import or an export later.
//
// ⚠ `onFinished` fires only for a job this screen SAW running (one it started, or found running when it opened). A
//   report already finished when the screen opened does not fire it again, or every visit to the composer would
//   re-select the guests of an import from yesterday.
import { useCallback, useEffect, useRef, useState } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { usePageVisibilityContext } from "@/contexts/PageVisibilityContext";
import { BulkJob, BulkJobKind, BulkJobService, bulkJobIsOpen } from "@/utils/api/BulkJobService";

/** How often a running job is re-read while the tab is visible. */
export const BULK_JOB_POLL_MS = 2_000;

/** How many times a finished job's report is asked for before the screen is told without it. */
const REPORT_ATTEMPTS = 3;

export interface UseBulkJobsReturn {
  /** Undismissed jobs, newest first. */
  jobs: BulkJob[];
  /** The first read has come back (whatever it said). */
  isLoaded: boolean;
  /** The last read's refusal, if it failed. */
  lastError: OpResult | null;
  /** A job the screen just started — shown at once, and watched until it finishes. */
  track: (job: BulkJob) => void;
  /** Close a finished job's report. */
  dismiss: (id: string) => Promise<OpResult>;
  /** Which job's report is being closed right now (its button spins). */
  dismissingId: string | null;
  reload: () => Promise<void>;
}

/**
 * @param kind - Which jobs to show.
 * @param eventId - The event whose jobs to show. ⚠ `null` reads NOTHING (a screen that is not ready, or a scope this
 *   kind is not shown on); pass `undefined` for an account-wide kind.
 * @param onFinished - Called once per job this screen saw running, when it is DONE, with its full report (recordIds and
 *   the refused rows) — the composer selects the new guests, the wizard and the Guests page re-read their lists.
 */
export const useBulkJobs = (
  kind: BulkJobKind,
  eventId: string | null | undefined,
  onFinished?: (job: BulkJob) => void,
): UseBulkJobsReturn => {
  const { isVisible } = usePageVisibilityContext();
  const [jobs, setJobs] = useState<BulkJob[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  // How many reads have landed — what re-arms the poll.
  const [readCount, setReadCount] = useState(0);
  // The jobs this screen has seen still running — only these may fire `onFinished`.
  const watchedRef = useRef<Set<string>>(new Set());
  // …and the ones that already did, so a re-read of a finished report cannot fire it twice.
  const reportedRef = useRef<Set<string>>(new Set());
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  // ⚠ A read for the previous event must not land on this one's screen.
  const eventRef = useRef(eventId);
  eventRef.current = eventId;
  // ⚠ Only the NEWEST read may land: a poll and a "just started" re-read can overlap, and an older answer arriving last
  //   would move the progress backwards.
  const readCallRef = useRef(0);

  const reportFinished = useCallback(async (job: BulkJob) => {
    if (reportedRef.current.has(job.id)) {
      return;
    }

    reportedRef.current.add(job.id);

    // ⚠ The report carries `recordIds` — without it the composer would clear its filters and select nobody. A failed
    //   read is tried again a couple of times before the screen is told with what it has (review of #132).
    let full: BulkJob | undefined;

    for (let attempt = 0; attempt < REPORT_ATTEMPTS && !full; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, BULK_JOB_POLL_MS));
      }

      const res = await BulkJobService.report(job.id);

      full = res.code === OP_RESULT_CODES.OK ? ((res.getDataFirst?.() ?? (res as any).data?.[0]) as BulkJob | undefined) : undefined;
    }

    onFinishedRef.current?.(full ?? job);
  }, []);

  const reload = useCallback(async () => {
    const forEvent = eventId;

    if (forEvent === null) {
      return;
    }

    const call = ++readCallRef.current;
    const res = await BulkJobService.listFor(kind, forEvent);

    if (eventRef.current !== forEvent || call !== readCallRef.current) {
      return;
    }

    setIsLoaded(true);
    // ⚠ Every read that lands re-arms the poll — a FAILED one too. Keyed on the rows alone, one failed poll (nothing to
    //   set) stopped polling for good and left the card at "Adding guests — x of y" (review of #132).
    setReadCount((n) => n + 1);

    if (res.code !== OP_RESULT_CODES.OK) {
      // ⚠ A failed re-read keeps what is on screen — a job does not vanish because one poll failed.
      setLastError(res);

      return;
    }

    setLastError(null);
    const next = ((res as any).data ?? []) as BulkJob[];

    for (const job of next) {
      if (bulkJobIsOpen(job.jobState)) {
        watchedRef.current.add(job.id);
      } else if (watchedRef.current.has(job.id)) {
        void reportFinished(job);
      }
    }

    setJobs(next);
  }, [kind, eventId, reportFinished]);

  // A new event (or kind) starts from nothing: its own first read, its own watched set.
  useEffect(() => {
    setJobs([]);
    setIsLoaded(false);
    setLastError(null);
    watchedRef.current = new Set();
    reportedRef.current = new Set();
    void reload();
  }, [kind, eventId, reload]);

  const anyOpen = jobs.some((j) => bulkJobIsOpen(j.jobState));

  // Re-read while something is still running, and only while the tab is visible (a hidden tab's timer is not run).
  useEffect(() => {
    if (!anyOpen || !isVisible) {
      return;
    }

    const timer = window.setTimeout(() => void reload(), BULK_JOB_POLL_MS);

    return () => window.clearTimeout(timer);
  }, [anyOpen, isVisible, readCount, reload]);

  // Coming BACK to the tab re-reads at once — the job kept going while nobody watched. (Not on mount: the effect above
  // already read.)
  const wasVisibleRef = useRef(isVisible);

  useEffect(() => {
    if (isVisible && !wasVisibleRef.current) {
      void reload();
    }

    wasVisibleRef.current = isVisible;
  }, [isVisible, reload]);

  const track = useCallback(
    (job: BulkJob) => {
      if (!job?.id) {
        return;
      }

      watchedRef.current.add(job.id);
      setJobs((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
      void reload();
    },
    [reload],
  );

  const dismiss = useCallback(async (id: string) => {
    setDismissingId(id);
    const res = await BulkJobService.dismiss(id);
    setDismissingId(null);

    if (res.code === OP_RESULT_CODES.OK) {
      setJobs((prev) => prev.filter((j) => j.id !== id));
    }

    return res;
  }, []);

  return { jobs, isLoaded, lastError, track, dismiss, dismissingId, reload };
};
